import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import * as XLSX from 'xlsx';
import { 
  evaluateFormula, 
  debugFormula, 
  adaptFormulaForCell, 
  applyFormulaToSelection,
  buildDependencyGraph,
  getCalculationOrder 
} from '../utils/formulaEngine';
import { columnToIndex, indexToColumn, cellToAddress, addressToCell } from '../utils/cellUtils';

// Excel benzeri hücre veri yapısı
const createCell = (value = '', formula = '', format = {}) => ({
  value,
  formula,
  format: {
    bold: false,
    italic: false,
    underline: false,
    fontSize: 11,
    fontFamily: 'Arial',
    textColor: '#000000',
    backgroundColor: '#ffffff',
    horizontalAlign: 'left',
    verticalAlign: 'top',
    border: {
      top: { style: 'none', color: '#000000' },
      right: { style: 'none', color: '#000000' },
      bottom: { style: 'none', color: '#000000' },
      left: { style: 'none', color: '#000000' }
    },
    ...(format || {})
  }
});

// Sparse grid oluştur - sadece kullanılan hücreleri tut
const createEmptyGrid = (rows = 100, cols = 100) => {
  const grid = [];
  for (let i = 0; i < rows; i++) {
    const row = [];
    for (let j = 0; j < cols; j++) {
      row.push(createCell());
    }
    grid.push(row);
  }
  return grid;
};

// Bu fonksiyonlar artık cellUtils.js'den import ediliyor

// Formül hesaplama motoru artık formulaEngine.js'den import ediliyor

// Bağımlı hücreleri yeniden hesapla
const recalculateDependentCells = (grid, changedRow, changedCol) => {
  const changedAddress = cellToAddress(changedRow, changedCol);
  const recalculatedCells = new Set(); // Sonsuz döngüyü önlemek için
  
  // Tüm grid'i tara ve bu hücreye referans veren formülleri bul
  for (let row = 0; row < grid.length; row++) {
    for (let col = 0; col < grid[row].length; col++) {
      const cell = grid[row][col];
      
      // Hücre var mı ve formülü var mı kontrol et
      if (cell && cell.formula && cell.formula.includes(changedAddress)) {
        const cellKey = `${row}-${col}`;
        if (!recalculatedCells.has(cellKey)) {
          try {
            // Formülü yeniden hesapla
            const newValue = evaluateFormula(cell.formula, grid);
            grid[row][col].value = newValue;
            recalculatedCells.add(cellKey);
            
            // Bu hücrenin de bağımlı hücrelerini hesapla (zincirleme hesaplama)
            recalculateDependentCells(grid, row, col);
          } catch (error) {
            grid[row][col].value = '#ERROR!';
          }
        }
      }
    }
  }
};

// Tüm formülleri yeniden hesapla (Excel'deki F9 gibi)
const recalculateAllFormulas = (grid) => {
  console.log('Tüm formüller yeniden hesaplanıyor...');
  
  // Önce tüm formülleri topla
  const formulaCells = [];
  for (let row = 0; row < grid.length; row++) {
    for (let col = 0; col < grid[row].length; col++) {
      const cell = grid[row][col];
      if (cell && cell.formula) {
        formulaCells.push({ row, col, formula: cell.formula });
      }
    }
  }
  
  // Formülleri bağımlılık sırasına göre sırala (basit yaklaşım)
  formulaCells.sort((a, b) => {
    // A1, B1, C1... sırasına göre sırala
    if (a.row !== b.row) return a.row - b.row;
    return a.col - b.col;
  });
  
  // Formülleri yeniden hesapla
  formulaCells.forEach(({ row, col, formula }) => {
    try {
      const newValue = evaluateFormula(formula, grid);
      grid[row][col].value = newValue;
    } catch (error) {
      console.error(`Formül hatası (${row},${col}):`, error);
      grid[row][col].value = '#ERROR!';
    }
  });
};

export const useSpreadsheetStore = create(
  devtools(
    (set, get) => ({
      // State
      sheets: [
        {
          id: 'sheet1',
          name: 'Sayfa 1',
          grid: createEmptyGrid(),
          activeCell: { row: 0, col: 0 },
          selection: { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
          frozenRows: 0,
          frozenCols: 0,
          columnWidths: {},
          rowHeights: {},
        }
      ],
      activeSheetIndex: 0,
      clipboard: null,
      undoStack: [],
      redoStack: [],
      isEditing: false,
      isLoading: false,
      formulaBarValue: '',
      currentFormId: null,
      isLoaded: false,
      tableTitle: '',
      tableDescription: '',

      // Actions
      setActiveCell: (row, col) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          activeCell: { row, col },
          selection: { startRow: row, startCol: col, endRow: row, endCol: col }
        };
        set({ sheets: newSheets });
      },

      setSelection: (startRow, startCol, endRow, endCol) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          selection: { startRow, startCol, endRow, endCol }
        };
        set({ sheets: newSheets });
      },

      updateCell: (row, col, value, formula = '') => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        const newGrid = newSheets[activeSheetIndex].grid.map(row => [...row]);
        
        // Grid sınırları kontrolü
        if (row < 0 || col < 0 || row >= newGrid.length || col >= newGrid[0].length) {
          console.error('Geçersiz hücre koordinatları:', row, col);
          return;
        }
        
        // Undo için mevcut durumu kaydet
        const currentValue = newGrid[row][col] || createCell();
        
        // Formül kontrolü
        let finalValue = value;
        let finalFormula = '';
        
        if (value && value.startsWith('=')) {
          finalFormula = value;
          try {
            finalValue = evaluateFormula(value, newGrid);
          } catch (error) {
            console.error('Formül değerlendirme hatası:', error);
            finalValue = '#ERROR!';
          }
        }
        
        newGrid[row][col] = createCell(finalValue, finalFormula, currentValue.format);
        
        // Bağımlı hücreleri yeniden hesapla
        recalculateDependentCells(newGrid, row, col);
        
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          grid: newGrid
        };
        
        set({ sheets: newSheets });
      },

      updateCellFormat: (row, col, format) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        const newGrid = newSheets[activeSheetIndex].grid.map(row => [...row]);
        
        newGrid[row][col] = {
          ...newGrid[row][col],
          format: { ...newGrid[row][col].format, ...format }
        };
        
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          grid: newGrid
        };
        
        set({ sheets: newSheets });
      },

      insertRow: (rowIndex) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        const newGrid = [...newSheets[activeSheetIndex].grid];
        
        const emptyRow = Array(26).fill(null).map(() => createCell());
        newGrid.splice(rowIndex, 0, emptyRow);
        
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          grid: newGrid
        };
        
        set({ sheets: newSheets });
      },

      deleteRow: (rowIndex) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        const newGrid = [...newSheets[activeSheetIndex].grid];
        
        if (rowIndex >= 0 && rowIndex < newGrid.length) {
          newGrid.splice(rowIndex, 1);
          
          // Yeni boş satır ekle (grid boyutunu koru)
          const emptyRow = Array(newGrid[0]?.length || 26).fill(null).map(() => createCell());
          newGrid.push(emptyRow);
        }
        
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          grid: newGrid
        };
        
        set({ sheets: newSheets });
        console.log('🗑️ Satır silindi:', rowIndex);
      },

      insertColumn: (colIndex) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        const newGrid = newSheets[activeSheetIndex].grid.map(row => [...row]);
        
        newGrid.forEach(row => {
          row.splice(colIndex, 0, createCell());
        });
        
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          grid: newGrid
        };
        
        set({ sheets: newSheets });
      },

      deleteColumn: (colIndex) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        const newGrid = newSheets[activeSheetIndex].grid.map(row => [...row]);
        
        if (colIndex >= 0) {
          newGrid.forEach(row => {
            if (colIndex < row.length) {
              row.splice(colIndex, 1);
              // Yeni boş hücre ekle (grid boyutunu koru)
              row.push(createCell());
            }
          });
        }
        
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          grid: newGrid
        };
        
        set({ sheets: newSheets });
        console.log('🗑️ Sütun silindi:', colIndex);
      },

      copySelection: () => {
        const { sheets, activeSheetIndex } = get();
        const { selection } = sheets[activeSheetIndex];
        const { grid } = sheets[activeSheetIndex];
        
        const copiedData = [];
        for (let i = selection.startRow; i <= selection.endRow; i++) {
          const row = [];
          for (let j = selection.startCol; j <= selection.endCol; j++) {
            const cell = grid[i]?.[j] || createCell();
            // Hücrenin tam kopyasını oluştur (value, formula, format)
            row.push({
              value: cell.value || '',
              formula: cell.formula || '',
              format: { ...cell.format }
            });
          }
          copiedData.push(row);
        }
        
        // Clipboard'a kopyala (tarayıcı clipboard API'si)
        if (navigator.clipboard && window.isSecureContext) {
          const textContent = copiedData.map(row => 
            row.map(cell => cell.value || '').join('\t')
          ).join('\n');
          
          navigator.clipboard.writeText(textContent).catch(err => {
            console.warn('Clipboard write failed:', err);
          });
        }
        
        set({ clipboard: copiedData });
        console.log('📋 Kopyalandı:', copiedData.length, 'satır x', copiedData[0]?.length || 0, 'sütun');
      },

      // Dış kaynaklardan yapıştırma için gelişmiş fonksiyon
      pasteFromClipboard: async (startRow, startCol) => {
        try {
          let textData = '';
          
          // Modern clipboard API'si ile dış veriyi al
          if (navigator.clipboard && navigator.clipboard.readText) {
            textData = await navigator.clipboard.readText();
          }
          
          if (!textData) {
            console.warn('Panoda veri bulunamadı');
            return;
          }
          
          console.log('📋 Dış veriden yapıştırılıyor:', textData);
          
          const { sheets, activeSheetIndex } = get();
          const newSheets = [...sheets];
          const newGrid = newSheets[activeSheetIndex].grid.map(row => [...row]);
          
          // Metni satır ve sütunlara ayır
          const rows = textData.split('\n').filter(row => row.length > 0);
          
          rows.forEach((rowText, rowIndex) => {
            // Tab veya virgül ile ayrılmış değerleri kontrol et
            const cells = rowText.includes('\t') ? rowText.split('\t') : rowText.split(',');
            
            cells.forEach((cellValue, colIndex) => {
              const targetRow = startRow + rowIndex;
              const targetCol = startCol + colIndex;
              
              // Grid sınırları kontrolü
              if (targetRow >= 0 && targetCol >= 0 && 
                  targetRow < newGrid.length && targetCol < newGrid[0].length) {
                
                const currentCell = newGrid[targetRow][targetCol] || createCell();
                const cleanValue = cellValue.trim();
                
                newGrid[targetRow][targetCol] = {
                  ...currentCell,
                  value: cleanValue,
                  formula: '', // Dış veriden gelen normal değer
                  displayValue: cleanValue
                };
              }
            });
          });
          
          // Formülleri yeniden hesapla
          recalculateAllFormulas(newGrid);
          
          newSheets[activeSheetIndex] = {
            ...newSheets[activeSheetIndex],
            grid: newGrid
          };
          
          set({ sheets: newSheets });
          console.log(`✅ Dış veriden ${rows.length} satır yapıştırıldı`);
          
        } catch (error) {
          console.error('❌ Dış veri yapıştırma hatası:', error);
        }
      },

      pasteSelection: (startRow, startCol) => {
        const { sheets, activeSheetIndex, clipboard } = get();
        if (!clipboard || clipboard.length === 0) {
          console.warn('Clipboard boş!');
          return;
        }
        
        const newSheets = [...sheets];
        const newGrid = newSheets[activeSheetIndex].grid.map(row => [...row]);
        
        // Clipboard verilerini yapıştır
        clipboard.forEach((row, rowIndex) => {
          row.forEach((cellData, colIndex) => {
            const targetRow = startRow + rowIndex;
            const targetCol = startCol + colIndex;
            
            // Grid sınırları kontrolü
            if (targetRow >= 0 && targetCol >= 0 && 
                targetRow < newGrid.length && targetCol < newGrid[0].length) {
              
              // Mevcut hücrenin formatını koru, sadece değeri güncelle (normal paste)
              const currentCell = newGrid[targetRow][targetCol] || createCell();
              
              newGrid[targetRow][targetCol] = {
                ...currentCell,
                value: cellData.value || '',
                formula: cellData.formula || '',
                format: { ...cellData.format } // Format'ı da kopyala
              };
              
              // Eğer formül varsa, yeniden hesapla
              if (cellData.formula && cellData.formula.startsWith('=')) {
                try {
                  const newValue = evaluateFormula(cellData.formula, newGrid);
                  newGrid[targetRow][targetCol].value = newValue;
                } catch (error) {
                  console.error('Formül hatası:', error);
                  newGrid[targetRow][targetCol].value = '#ERROR!';
                }
              }
            }
          });
        });
        
        // Bağımlı hücreleri yeniden hesapla
        recalculateAllFormulas(newGrid);
        
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          grid: newGrid
        };
        
        set({ sheets: newSheets });
        console.log('📋 Yapıştırıldı:', clipboard.length, 'satır x', clipboard[0]?.length || 0, 'sütun');
      },

      setColumnWidth: (colIndex, width) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          columnWidths: {
            ...newSheets[activeSheetIndex].columnWidths,
            [colIndex]: width
          }
        };
        set({ sheets: newSheets });
      },

      setRowHeight: (rowIndex, height) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          rowHeights: {
            ...newSheets[activeSheetIndex].rowHeights,
            [rowIndex]: height
          }
        };
        set({ sheets: newSheets });
      },

      addSheet: () => {
        const { sheets } = get();
        const newSheet = {
          id: `sheet${sheets.length + 1}`,
          name: `Sayfa ${sheets.length + 1}`,
          grid: createEmptyGrid(),
          activeCell: { row: 0, col: 0 },
          selection: { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
          frozenRows: 0,
          frozenCols: 0,
          columnWidths: {},
          rowHeights: {}
        };
        set({ 
          sheets: [...sheets, newSheet],
          activeSheetIndex: sheets.length
        });
      },

      renameSheet: (sheetIndex, newName) => {
        const { sheets } = get();
        if (sheetIndex >= 0 && sheetIndex < sheets.length) {
          const newSheets = [...sheets];
          newSheets[sheetIndex] = {
            ...newSheets[sheetIndex],
            name: newName
          };
          set({ sheets: newSheets });
        }
      },

      deleteSheet: (sheetIndex) => {
        const { sheets, activeSheetIndex } = get();
        if (sheets.length <= 1) {
          alert('En az bir sayfa olmalıdır!');
          return;
        }
        
        if (sheetIndex >= 0 && sheetIndex < sheets.length) {
          const newSheets = sheets.filter((_, index) => index !== sheetIndex);
          let newActiveIndex = activeSheetIndex;
          
          // Eğer silinen sayfa aktif sayfaysa, önceki sayfaya geç
          if (sheetIndex === activeSheetIndex) {
            newActiveIndex = Math.max(0, activeSheetIndex - 1);
          } else if (sheetIndex < activeSheetIndex) {
            // Eğer silinen sayfa aktif sayfadan önceyse, aktif sayfa indeksini güncelle
            newActiveIndex = activeSheetIndex - 1;
          }
          
          set({ 
            sheets: newSheets,
            activeSheetIndex: newActiveIndex
          });
        }
      },

      setActiveSheet: (index) => {
        set({ activeSheetIndex: index });
      },

      setEditing: (isEditing) => {
        set({ isEditing });
      },

      setFormulaBarValue: (value) => {
        set({ formulaBarValue: value });
      },

      // Excel import/export
      importExcel: (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          let totalStyledCells = 0; // Toplam stilli hücre sayısını takip et
          
          const sheets = workbook.SheetNames.map((sheetName, index) => {
            const worksheet = workbook.Sheets[sheetName];
            
            // Excel'den hem değerleri hem de formülleri al
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            
            // Hücre stillerini al
            const cellStyles = {};
            if (worksheet['!ref']) {
              const range = XLSX.utils.decode_range(worksheet['!ref']);
              for (let row = range.s.r; row <= range.e.r; row++) {
                for (let col = range.s.c; col <= range.e.c; col++) {
                  const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                  const cell = worksheet[cellAddress];
                  if (cell && cell.s) {
                    // Excel renk formatını düzelt
                    const getColorFromExcel = (colorObj) => {
                      if (!colorObj) return null;
                      
                      // RGB formatı varsa
                      if (colorObj.rgb) {
                        const rgb = colorObj.rgb.toString().toUpperCase();
                        // ARGB formatından RGB'ye çevir
                        if (rgb.length === 8) {
                          return `#${rgb.substring(2)}`;
                        }
                        // RGB formatı
                        if (rgb.length === 6) {
                          return `#${rgb}`;
                        }
                      }
                      
                      // Tema rengi varsa
                      if (colorObj.theme !== undefined) {
                        // Excel tema renklerini varsayılan renklere çevir
                        const themeColors = {
                          0: '#FFFFFF', // Beyaz
                          1: '#000000', // Siyah
                          2: '#E7E6E6', // Açık gri
                          3: '#44546A', // Koyu gri
                          4: '#5B9BD5', // Mavi
                          5: '#70AD47', // Yeşil
                          6: '#FFC000', // Sarı
                          7: '#C5504B', // Kırmızı
                          8: '#264478', // Koyu mavi
                          9: '#385723'  // Koyu yeşil
                        };
                        return themeColors[colorObj.theme] || '#000000';
                      }
                      
                      return null;
                    };
                    
                    const style = {
                      bold: cell.s?.font?.bold || false,
                      italic: cell.s?.font?.italic || false,
                      underline: cell.s?.font?.underline || false,
                      fontSize: cell.s?.font?.sz || 11,
                      fontFamily: cell.s?.font?.name || 'Arial',
                      textColor: getColorFromExcel(cell.s?.font?.color) || '#000000',
                      backgroundColor: getColorFromExcel(cell.s?.fill?.fgColor) || '#ffffff',
                      horizontalAlign: cell.s?.alignment?.horizontal || 'left',
                      verticalAlign: cell.s?.alignment?.vertical || 'top',
                      border: {
                        top: { 
                          style: cell.s?.border?.top?.style || 'none', 
                          color: getColorFromExcel(cell.s?.border?.top?.color) || '#000000'
                        },
                        right: { 
                          style: cell.s?.border?.right?.style || 'none', 
                          color: getColorFromExcel(cell.s?.border?.right?.color) || '#000000'
                        },
                        bottom: { 
                          style: cell.s?.border?.bottom?.style || 'none', 
                          color: getColorFromExcel(cell.s?.border?.bottom?.color) || '#000000'
                        },
                        left: { 
                          style: cell.s?.border?.left?.style || 'none', 
                          color: getColorFromExcel(cell.s?.border?.left?.color) || '#000000'
                        }
                      }
                    };
                    
                    cellStyles[`${row}-${col}`] = style;
                    totalStyledCells++; // Stilli hücre sayısını artır
                    
                                         // Debug: Stil bilgisini logla (sadece geliştirme modunda)
                     if (process.env.NODE_ENV === 'development' && (style.bold || style.italic || style.backgroundColor !== '#ffffff' || style.textColor !== '#000000')) {
                       console.log(`🎨 Stil bulundu: ${cellAddress}`, style);
                     }
                  }
                }
              }
            }
            
            // JSON veriyi grid formatına çevir
            const grid = createEmptyGrid();
            
            // Maksimum satır ve sütun sayısını bul
            let maxRow = 0;
            let maxCol = 0;
            
            jsonData.forEach((row, rowIndex) => {
              if (row && Array.isArray(row)) {
                maxRow = Math.max(maxRow, rowIndex);
                row.forEach((cell, colIndex) => {
                  if (cell !== undefined && cell !== null) {
                    maxCol = Math.max(maxCol, colIndex);
                  }
                });
              }
            });
            
            // Grid boyutunu gerekirse genişlet - sadece gerekli kadar
            const requiredRows = Math.max(maxRow + 1, 50); // En az 50 satır
            const requiredCols = Math.max(maxCol + 1, 50); // En az 50 sütun
            
            // Grid'i sadece gerekli boyutta genişlet
            while (grid.length < requiredRows) {
              const newRow = [];
              for (let j = 0; j < requiredCols; j++) {
                newRow.push(createCell());
              }
              grid.push(newRow);
            }
            
            // Mevcut satırları genişlet
            for (let i = 0; i < grid.length; i++) {
              while (grid[i].length < requiredCols) {
                grid[i].push(createCell());
              }
            }
            
            // Verileri grid'e yerleştir
            jsonData.forEach((row, rowIndex) => {
              if (row && Array.isArray(row)) {
                row.forEach((cell, colIndex) => {
                  if (cell !== undefined && cell !== null) {
                    // Hücre değerini ve formülünü al
                    let cellValue = cell.toString();
                    let cellFormula = '';
                    
                    // Excel'den formül bilgisini al
                    const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
                    const excelCell = worksheet[cellAddress];
                    
                    if (excelCell && excelCell.f) {
                      // Excel formülünü bizim formatımıza çevir
                      cellFormula = '=' + excelCell.f;
                      
                      // İlk önce Excel'den gelen değeri kullan, sonra formülü yeniden hesaplayacağız
                      cellValue = excelCell.v !== undefined ? excelCell.v.toString() : '';
                      
                                             // Debug: Formül bilgisini logla (sadece geliştirme modunda)
                       if (process.env.NODE_ENV === 'development') {
                         console.log(`📊 Formül bulundu: ${cellAddress} = ${cellFormula} (Excel değeri: ${cellValue})`);
                       }
                    }
                    
                    // Hücre stilini al
                    const cellStyle = cellStyles[`${rowIndex}-${colIndex}`] || {};
                    
                    // Hücreyi oluştur
                    grid[rowIndex][colIndex] = createCell(cellValue, cellFormula, cellStyle);
                  }
                });
              }
            });
            
            // Debug: Grid boyutu (sadece geliştirme modunda)
            if (process.env.NODE_ENV === 'development') {
              console.log(`📊 Grid boyutu: ${requiredRows} satır x ${requiredCols} sütun`);
            }
            
            // Sütun genişliklerini al
            const columnWidths = {};
            if (worksheet['!cols']) {
              worksheet['!cols'].forEach((col, index) => {
                if (col && col.width) {
                  columnWidths[index] = col.width * 7; // Excel genişliğini piksel'e çevir
                }
              });
            }
            
            // Satır yüksekliklerini al
            const rowHeights = {};
            if (worksheet['!rows']) {
              worksheet['!rows'].forEach((row, index) => {
                if (row && row.hpt) {
                  rowHeights[index] = row.hpt * 0.75; // Excel yüksekliğini piksel'e çevir
                }
              });
            }
            
            // Birleştirilmiş hücreleri al
            const mergedRanges = [];
            if (worksheet['!merges']) {
              worksheet['!merges'].forEach(merge => {
                const mergeRange = {
                  startRow: merge.s.r,
                  startCol: merge.s.c,
                  endRow: merge.e.r,
                  endCol: merge.e.c
                };
                mergedRanges.push(mergeRange);
                
                // Debug: Birleştirilmiş hücre bilgisini logla
                console.log(`🔗 Birleştirilmiş hücre: ${merge.s.r}-${merge.s.c} to ${merge.e.r}-${merge.e.c}`);
              });
            }
            
            return {
              id: `sheet${index + 1}`,
              name: sheetName,
              grid,
              activeCell: { row: 0, col: 0 },
              selection: { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
              frozenRows: 0,
              frozenCols: 0,
              columnWidths,
              rowHeights,
              mergedRanges
            };
          });
          
          set({ sheets, activeSheetIndex: 0 });
          
          console.log('✅ Excel dosyası başarıyla import edildi!');
          console.log(`📊 Toplam ${sheets.length} sayfa, ${totalStyledCells} stilli hücre import edildi`);
          
          // Import sonrası tüm formülleri yeniden hesapla
          setTimeout(() => {
            const { sheets: currentSheets } = get();
            const newSheets = currentSheets.map(sheet => {
              const newGrid = sheet.grid.map(row => [...row]);
              
              console.log('🔄 Import sonrası formüller yeniden hesaplanıyor...');
              
              // Tüm formülleri tekrar hesapla
              for (let row = 0; row < newGrid.length; row++) {
                for (let col = 0; col < newGrid[row].length; col++) {
                  const cell = newGrid[row][col];
                  if (cell && cell.formula && cell.formula.startsWith('=')) {
                    try {
                      const newValue = evaluateFormula(cell.formula, newGrid);
                      newGrid[row][col].value = newValue;
                      console.log(`✅ Formül hesaplandı: ${cell.formula} = ${newValue}`);
                    } catch (error) {
                      newGrid[row][col].value = '#ERROR!';
                      console.error(`❌ Formül hatası: ${cell.formula}`, error);
                    }
                  }
                }
              }
              
              recalculateAllFormulas(newGrid);
              
              // Birleştirilmiş hücreleri uygula
              if (sheet.mergedRanges && Array.isArray(sheet.mergedRanges)) {
                sheet.mergedRanges.forEach(merge => {
                  if (merge && typeof merge === 'object') {
                    const { startRow, startCol, endRow, endCol } = merge;
                    
                    // Geçerli koordinat kontrolü
                    if (typeof startRow === 'number' && typeof startCol === 'number' && 
                        typeof endRow === 'number' && typeof endCol === 'number' &&
                        startRow >= 0 && startCol >= 0 && 
                        endRow < newGrid.length && endCol < newGrid[0].length) {
                      
                      for (let i = startRow; i <= endRow; i++) {
                        for (let j = startCol; j <= endCol; j++) {
                          if (newGrid[i] && newGrid[i][j]) {
                            if (i === startRow && j === startCol) {
                              // Ana hücre
                              newGrid[i][j] = {
                                ...newGrid[i][j],
                                format: {
                                  ...(newGrid[i][j].format || {}),
                                  merged: true,
                                  mergedRange: { startRow, startCol, endRow, endCol }
                                }
                              };
                            } else {
                              // Birleştirilen hücreler
                              newGrid[i][j] = {
                                ...newGrid[i][j],
                                value: '',
                                format: {
                                  ...(newGrid[i][j].format || {}),
                                  merged: true,
                                  mergedParent: { row: startRow, col: startCol }
                                }
                              };
                            }
                          }
                        }
                      }
                    }
                  }
                });
              }
              
              return {
                ...sheet,
                grid: newGrid
              };
            });
            
            set({ sheets: newSheets });
          }, 100);
        };
        reader.readAsArrayBuffer(file);
      },

      exportExcel: () => {
        const { sheets } = get();
        const workbook = XLSX.utils.book_new();
        
        sheets.forEach(sheet => {
          // Grid veriyi Excel formatına çevir
          const excelData = [];
          const cellStyles = {};
          
          // Maksimum satır ve sütun sayısını bul
          let maxRow = 0;
          let maxCol = 0;
          
          sheet.grid.forEach((row, rowIndex) => {
            const excelRow = [];
            row.forEach((cell, colIndex) => {
              if (cell && (cell.value || cell.formula)) {
                maxRow = Math.max(maxRow, rowIndex);
                maxCol = Math.max(maxCol, colIndex);
                
                // Hücre değerini ekle
                if (cell.formula) {
                  excelRow[colIndex] = { f: cell.formula, v: cell.value };
                } else {
                  excelRow[colIndex] = cell.value || '';
                }
                
                // Hücre stilini ekle
                if (cell.format && typeof cell.format === 'object') {
                  const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
                  cellStyles[cellAddress] = {
                    font: {
                      bold: cell.format.bold || false,
                      italic: cell.format.italic || false,
                      underline: cell.format.underline || false,
                      sz: cell.format.fontSize || 11,
                      name: cell.format.fontFamily || 'Arial',
                      color: { rgb: cell.format.textColor?.replace('#', '') || '000000' }
                    },
                    fill: {
                      fgColor: { rgb: cell.format.backgroundColor?.replace('#', '') || 'FFFFFF' }
                    },
                    alignment: {
                      horizontal: cell.format.horizontalAlign || 'left',
                      vertical: cell.format.verticalAlign || 'top'
                    },
                    border: {
                      top: { 
                        style: cell.format.border?.top?.style || 'none',
                        color: { rgb: cell.format.border?.top?.color?.replace('#', '') || '000000' }
                      },
                      right: { 
                        style: cell.format.border?.right?.style || 'none',
                        color: { rgb: cell.format.border?.right?.color?.replace('#', '') || '000000' }
                      },
                      bottom: { 
                        style: cell.format.border?.bottom?.style || 'none',
                        color: { rgb: cell.format.border?.bottom?.color?.replace('#', '') || '000000' }
                      },
                      left: { 
                        style: cell.format.border?.left?.style || 'none',
                        color: { rgb: cell.format.border?.left?.color?.replace('#', '') || '000000' }
                      }
                    }
                  };
                }
              }
            });
            excelData.push(excelRow);
          });
          
          const worksheet = XLSX.utils.aoa_to_sheet(excelData);
          
          // Hücre stillerini uygula
          Object.keys(cellStyles).forEach(cellAddress => {
            if (worksheet[cellAddress] && cellStyles[cellAddress]) {
              worksheet[cellAddress].s = cellStyles[cellAddress];
            }
          });
          
          // Sütun genişliklerini ayarla
          if (Object.keys(sheet.columnWidths).length > 0) {
            worksheet['!cols'] = [];
            for (let i = 0; i <= maxCol; i++) {
              const width = sheet.columnWidths[i] || 120;
              worksheet['!cols'].push({ width: width / 7 }); // Piksel'i Excel genişliğine çevir
            }
          }
          
          // Satır yüksekliklerini ayarla
          if (Object.keys(sheet.rowHeights).length > 0) {
            worksheet['!rows'] = [];
            for (let i = 0; i <= maxRow; i++) {
              const height = sheet.rowHeights[i] || 25;
              worksheet['!rows'].push({ hpt: height / 0.75 }); // Piksel'i Excel yüksekliğine çevir
            }
          }
          
          // Birleştirilmiş hücreleri ekle
          const mergedRanges = [];
          const processedMerges = new Set();
          
          sheet.grid.forEach((row, rowIndex) => {
            row.forEach((cell, colIndex) => {
              if (cell && cell.format && cell.format.merged && cell.format.mergedRange) {
                const mergeKey = `${cell.format.mergedRange.startRow}-${cell.format.mergedRange.startCol}-${cell.format.mergedRange.endRow}-${cell.format.mergedRange.endCol}`;
                
                if (!processedMerges.has(mergeKey)) {
                  mergedRanges.push({
                    s: { r: cell.format.mergedRange.startRow, c: cell.format.mergedRange.startCol },
                    e: { r: cell.format.mergedRange.endRow, c: cell.format.mergedRange.endCol }
                  });
                  processedMerges.add(mergeKey);
                }
              }
            });
          });
          
          if (mergedRanges.length > 0) {
            worksheet['!merges'] = mergedRanges;
          }
          
          XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
        });
        
        // Dosyayı indir
        XLSX.writeFile(workbook, 'spreadsheet.xlsx');
      },

      exportExcelToCloud: async (appToken, userData) => {
        // Token kontrolü
        if (!appToken) {
          throw new Error('ApplicationToken bulunamadı. Lütfen tekrar giriş yapın.');
        }
        
        if (!userData || !userData.id) {
          throw new Error('Kullanıcı bilgisi bulunamadı. Lütfen tekrar giriş yapın.');
        }
        
        const { sheets } = get();
        const workbook = XLSX.utils.book_new();
        
        sheets.forEach(sheet => {
          // Grid veriyi Excel formatına çevir
          const excelData = [];
          const cellStyles = {};
          
          // Maksimum satır ve sütun sayısını bul
          let maxRow = 0;
          let maxCol = 0;
          
          sheet.grid.forEach((row, rowIndex) => {
            const excelRow = [];
            row.forEach((cell, colIndex) => {
              if (cell && (cell.value || cell.formula)) {
                maxRow = Math.max(maxRow, rowIndex);
                maxCol = Math.max(maxCol, colIndex);
                
                // Hücre değerini ekle
                if (cell.formula) {
                  excelRow[colIndex] = { f: cell.formula, v: cell.value };
                } else {
                  excelRow[colIndex] = cell.value || '';
                }
                
                // Hücre stilini ekle
                if (cell.format && typeof cell.format === 'object') {
                  const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
                  cellStyles[cellAddress] = {
                    font: {
                      bold: cell.format.bold || false,
                      italic: cell.format.italic || false,
                      underline: cell.format.underline || false,
                      sz: cell.format.fontSize || 11,
                      name: cell.format.fontFamily || 'Arial',
                      color: { rgb: cell.format.textColor?.replace('#', '') || '000000' }
                    },
                    fill: {
                      fgColor: { rgb: cell.format.backgroundColor?.replace('#', '') || 'FFFFFF' }
                    },
                    alignment: {
                      horizontal: cell.format.horizontalAlign || 'left',
                      vertical: cell.format.verticalAlign || 'top'
                    },
                    border: {
                      top: { 
                        style: cell.format.border?.top?.style || 'none',
                        color: { rgb: cell.format.border?.top?.color?.replace('#', '') || '000000' }
                      },
                      right: { 
                        style: cell.format.border?.right?.style || 'none',
                        color: { rgb: cell.format.border?.right?.color?.replace('#', '') || '000000' }
                      },
                      bottom: { 
                        style: cell.format.border?.bottom?.style || 'none',
                        color: { rgb: cell.format.border?.bottom?.color?.replace('#', '') || '000000' }
                      },
                      left: { 
                        style: cell.format.border?.left?.style || 'none',
                        color: { rgb: cell.format.border?.left?.color?.replace('#', '') || '000000' }
                      }
                    }
                  };
                }
              }
            });
            excelData.push(excelRow);
          });
          
          const worksheet = XLSX.utils.aoa_to_sheet(excelData);
          
          // Hücre stillerini uygula
          Object.keys(cellStyles).forEach(cellAddress => {
            if (worksheet[cellAddress] && cellStyles[cellAddress]) {
              worksheet[cellAddress].s = cellStyles[cellAddress];
            }
          });
          
          // Sütun genişliklerini ayarla
          if (Object.keys(sheet.columnWidths).length > 0) {
            worksheet['!cols'] = [];
            for (let i = 0; i <= maxCol; i++) {
              const width = sheet.columnWidths[i] || 120;
              worksheet['!cols'].push({ width: width / 7 }); // Piksel'i Excel genişliğine çevir
            }
          }
          
          // Satır yüksekliklerini ayarla
          if (Object.keys(sheet.rowHeights).length > 0) {
            worksheet['!rows'] = [];
            for (let i = 0; i <= maxRow; i++) {
              const height = sheet.rowHeights[i] || 25;
              worksheet['!rows'].push({ hpt: height / 0.75 }); // Piksel'i Excel yüksekliğine çevir
            }
          }
          
          // Birleştirilmiş hücreleri ekle
          const mergedRanges = [];
          const processedMerges = new Set();
          
          sheet.grid.forEach((row, rowIndex) => {
            row.forEach((cell, colIndex) => {
              if (cell && cell.format && cell.format.merged && cell.format.mergedRange) {
                const mergeKey = `${cell.format.mergedRange.startRow}-${cell.format.mergedRange.startCol}-${cell.format.mergedRange.endRow}-${cell.format.mergedRange.endCol}`;
                
                if (!processedMerges.has(mergeKey)) {
                  mergedRanges.push({
                    s: { r: cell.format.mergedRange.startRow, c: cell.format.mergedRange.startCol },
                    e: { r: cell.format.mergedRange.endRow, c: cell.format.mergedRange.endCol }
                  });
                  processedMerges.add(mergeKey);
                }
              }
            });
          });
          
          if (mergedRanges.length > 0) {
            worksheet['!merges'] = mergedRanges;
          }
          
          XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
        });
        
        console.log("workbook", workbook);
        
        // Excel dosyasını blob olarak oluştur
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        // FormData oluştur
        const formData = new FormData();
        formData.append("file", blob, "spreadsheet.xlsx");
        
        // API'ye gönderilecek veri
        const spreadsheetData = {
          parentId: "0", // Varsayılan klasör
          formId: `spreadsheet_${Date.now()}`, // Benzersiz ID
          title: "Spreadsheet", // Varsayılan başlık
          formContent: {
            id: `spreadsheet_${Date.now()}`,
            title: "Spreadsheet",
            description: "Excel benzeri tablo",
            sheets: sheets,
            isPublished: true,
            publishedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            formLink: "",
            logo: ""
          },
          isActive: true,
          fileType: 1, // Spreadsheet tipi için fileType = 1
          documentPrefix: "",
          documentCode: "",
          department: ""
        };
        
        // API çağrısı yap
        try {
          const response = await fetch('https://app.hhoms.com/api/form/Libraries', {
            method: 'POST',
            headers: {
              'ApplicationToken': appToken,
              'User': userData.id,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(spreadsheetData)
          });
          
          if (response.ok) {
            console.log('Spreadsheet başarıyla kaydedildi');
            // Başarı mesajı göster
            alert('Spreadsheet başarıyla kaydedildi!');
          } else {
            console.error('Spreadsheet kaydedilirken hata oluştu');
            alert('Spreadsheet kaydedilirken hata oluştu!');
          }
        } catch (error) {
          console.error('API çağrısı hatası:', error);
          alert('API çağrısı sırasında hata oluştu!');
        }
      },

      // Utility functions
      getActiveSheet: () => {
        const { sheets, activeSheetIndex } = get();
        return sheets[activeSheetIndex];
      },

      getCellValue: (row, col) => {
        const { sheets, activeSheetIndex } = get();
        const cell = sheets[activeSheetIndex].grid[row]?.[col];
        if (!cell) return '';
        
        // Eğer formül varsa ve hesaplanmış değer yoksa, formülü hesapla
        if (cell.formula && cell.formula.startsWith('=')) {
          try {
            const calculatedValue = evaluateFormula(cell.formula, sheets[activeSheetIndex].grid);
            // DEBUG: Formül hesaplama sonucunu logla
            if (row < 25 && col < 10) {
              console.log(`🧮 Formula[${row},${col}]: ${cell.formula} = ${calculatedValue}`);
            }
            return calculatedValue;
          } catch (error) {
            console.error(`Formül hesaplama hatası [${row},${col}]:`, error);
            return '#ERROR!';
          }
        }
        
        // Normal değer
        const value = cell.value || '';
        return value;
      },

      getCellFormula: (row, col) => {
        const { sheets, activeSheetIndex } = get();
        return sheets[activeSheetIndex].grid[row]?.[col]?.formula || '';
      },

      getCellFormat: (row, col) => {
        const { sheets, activeSheetIndex } = get();
        const cell = sheets[activeSheetIndex].grid[row]?.[col];
        return cell?.format || {
          bold: false,
          italic: false,
          underline: false,
          fontSize: 11,
          fontFamily: 'Arial',
          textColor: '#000000',
          backgroundColor: '#ffffff',
          horizontalAlign: 'left',
          verticalAlign: 'top',
          border: {
            top: { style: 'none', color: '#000000' },
            right: { style: 'none', color: '#000000' },
            bottom: { style: 'none', color: '#000000' },
            left: { style: 'none', color: '#000000' }
          }
        };
      },

      // Hücre birleştirme fonksiyonları
      mergeCells: (startRow, startCol, endRow, endCol) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        const newGrid = newSheets[activeSheetIndex].grid.map(row => [...row]);
        
        // Grid sınırları kontrolü
        if (startRow < 0 || startCol < 0 || endRow >= newGrid.length || endCol >= newGrid[0].length) {
          console.error('Geçersiz birleştirme koordinatları:', startRow, startCol, endRow, endCol);
          return;
        }
        
        // Birleştirilecek hücrelerin değerlerini al
        const startCell = newGrid[startRow][startCol] || createCell();
        const mergedValue = startCell.value;
        const mergedFormat = startCell.format;
        
        // Birleştirilen hücreleri işaretle
        for (let i = startRow; i <= endRow; i++) {
          for (let j = startCol; j <= endCol; j++) {
            if (i === startRow && j === startCol) {
              // Ana hücre - değeri ve formatı koru
              newGrid[i][j] = {
                ...newGrid[i][j],
                value: mergedValue,
                format: {
                  ...mergedFormat,
                  merged: true,
                  mergedRange: { startRow, startCol, endRow, endCol }
                }
              };
            } else {
              // Birleştirilen hücreler - gizle
              newGrid[i][j] = {
                ...newGrid[i][j],
                value: '',
                format: {
                  ...newGrid[i][j].format,
                  merged: true,
                  mergedParent: { row: startRow, col: startCol }
                }
              };
            }
          }
        }
        
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          grid: newGrid,
          activeCell: { row: startRow, col: startCol },
          selection: { startRow, startCol, endRow, endCol }
        };
        
        set({ sheets: newSheets });
      },

      unmergeCells: (row, col) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        const newGrid = newSheets[activeSheetIndex].grid.map(row => [...row]);
        
        // Grid sınırları kontrolü
        if (row < 0 || col < 0 || row >= newGrid.length || col >= newGrid[0].length) {
          console.error('Geçersiz hücre koordinatları:', row, col);
          return;
        }
        
        const cell = newGrid[row][col];
        
        if (cell && cell.format && cell.format.merged && cell.format.mergedRange) {
          const { startRow, startCol, endRow, endCol } = cell.format.mergedRange;
          
          // Birleştirilen hücreleri geri ayır
          for (let i = startRow; i <= endRow; i++) {
            for (let j = startCol; j <= endCol; j++) {
              newGrid[i][j] = {
                ...newGrid[i][j],
                format: {
                  ...newGrid[i][j].format,
                  merged: false,
                  mergedRange: null,
                  mergedParent: null
                }
              };
            }
          }
        }
        
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          grid: newGrid
        };
        
        set({ sheets: newSheets });
      },

      isMergedCell: (row, col) => {
        const { sheets, activeSheetIndex } = get();
        const grid = sheets[activeSheetIndex].grid;
        if (!grid || row < 0 || col < 0 || row >= grid.length || col >= grid[0].length) {
          return false;
        }
        const cell = grid[row][col];
        return cell?.format?.merged || false;
      },

      getMergedRange: (row, col) => {
        const { sheets, activeSheetIndex } = get();
        const grid = sheets[activeSheetIndex].grid;
        if (!grid || row < 0 || col < 0 || row >= grid.length || col >= grid[0].length) {
          return null;
        }
        const cell = grid[row][col];
        return cell?.format?.mergedRange || null;
      },

      isMergedParent: (row, col) => {
        const { sheets, activeSheetIndex } = get();
        const grid = sheets[activeSheetIndex].grid;
        if (!grid || row < 0 || col < 0 || row >= grid.length || col >= grid[0].length) {
          return false;
        }
        const cell = grid[row][col];
        return cell?.format?.merged && cell?.format?.mergedRange;
      },

      // Sütun ve satır boyutlandırma
      setColumnWidth: (colIndex, width) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          columnWidths: {
            ...newSheets[activeSheetIndex].columnWidths,
            [colIndex]: Math.max(50, width) // Minimum 50px
          }
        };
        set({ sheets: newSheets });
      },

      setRowHeight: (rowIndex, height) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          rowHeights: {
            ...newSheets[activeSheetIndex].rowHeights,
            [rowIndex]: Math.max(20, height) // Minimum 20px
          }
        };
        set({ sheets: newSheets });
      },

      getColumnWidth: (colIndex) => {
        const { sheets, activeSheetIndex } = get();
        const sheet = sheets[activeSheetIndex];
        if (!sheet || !sheet.columnWidths) {
          return 120; // Varsayılan genişlik
        }
        return sheet.columnWidths[colIndex] || 120;
      },

      getRowHeight: (rowIndex) => {
        const { sheets, activeSheetIndex } = get();
        const sheet = sheets[activeSheetIndex];
        if (!sheet || !sheet.rowHeights) {
          return 25; // Varsayılan yükseklik
        }
        return sheet.rowHeights[rowIndex] || 25;
      },

      // Gelişmiş seçim fonksiyonları
      selectRow: (rowIndex) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          activeCell: { row: rowIndex, col: 0 },
          selection: { startRow: rowIndex, startCol: 0, endRow: rowIndex, endCol: 25 }
        };
        set({ sheets: newSheets });
      },

      selectColumn: (colIndex) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          activeCell: { row: 0, col: colIndex },
          selection: { startRow: 0, startCol: colIndex, endRow: 999, endCol: colIndex }
        };
        set({ sheets: newSheets });
      },

      selectAll: () => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          activeCell: { row: 0, col: 0 },
          selection: { startRow: 0, startCol: 0, endRow: 999, endCol: 25 }
        };
        set({ sheets: newSheets });
      },

      // Excel benzeri otomatik hesaplama fonksiyonları
      recalculateAllFormulas: () => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        const newGrid = newSheets[activeSheetIndex].grid.map(row => [...row]);
        
        recalculateAllFormulas(newGrid);
        
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          grid: newGrid
        };
        
        set({ sheets: newSheets });
      },

      // Formül güncelleme (Excel'deki gibi otomatik)
      updateFormula: (row, col, formula) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        const newGrid = newSheets[activeSheetIndex].grid.map(row => [...row]);
        
        // Grid sınırları kontrolü
        if (row < 0 || col < 0 || row >= newGrid.length || col >= newGrid[0].length) {
          console.error('Geçersiz hücre koordinatları:', row, col);
          return;
        }
        
        // Mevcut hücreyi al
        const currentCell = newGrid[row][col] || createCell();
        
        // Formülü güncelle
        let finalValue = formula;
        let finalFormula = '';
        
        if (formula && formula.startsWith('=')) {
          finalFormula = formula;
          try {
            finalValue = evaluateFormula(formula, newGrid);
            console.log(`✅ Formül güncellendi [${row},${col}]: ${formula} = ${finalValue}`);
          } catch (error) {
            console.error('Formül değerlendirme hatası:', error);
            finalValue = '#ERROR!';
          }
        } else {
          // Normal değer, formül değil
          finalValue = formula;
          finalFormula = '';
        }
        
        // Hücreyi güncelle
        newGrid[row][col] = createCell(finalValue, finalFormula, currentCell.format);
        
        // Bağımlı hücreleri yeniden hesapla (zincirleme)
        recalculateDependentCells(newGrid, row, col);
        
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          grid: newGrid
        };
        
        set({ sheets: newSheets });
      },

      // Hücre değeri değiştiğinde otomatik hesaplama
      updateCellValue: (row, col, value) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        const newGrid = newSheets[activeSheetIndex].grid.map(row => [...row]);
        
        // Grid sınırları kontrolü
        if (row < 0 || col < 0 || row >= newGrid.length || col >= newGrid[0].length) {
          console.error('Geçersiz hücre koordinatları:', row, col);
          return;
        }
        
        // Mevcut hücreyi al
        const currentCell = newGrid[row][col] || createCell();
        
        // Sadece değeri güncelle (formülü koru)
        newGrid[row][col] = createCell(value, currentCell.formula, currentCell.format);
        
        // Bağımlı hücreleri yeniden hesapla
        recalculateDependentCells(newGrid, row, col);
        
        newSheets[activeSheetIndex] = {
          ...newSheets[activeSheetIndex],
          grid: newGrid
        };
        
        set({ sheets: newSheets });
      },

      // Debug fonksiyonu - formül test etmek için
      debugFormula: (formula) => {
        const { sheets, activeSheetIndex } = get();
        const grid = sheets[activeSheetIndex].grid;
        return debugFormula(formula, grid);
      },

      // YENİ: Çoklu hücre seçimi için formül uygulama
      applyFormulaToSelection: (formula) => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        const sheet = newSheets[activeSheetIndex];
        const newGrid = sheet.grid.map(row => [...row]);
        
        // Seçili hücreleri al
        const { selection } = sheet;
        const { startRow, startCol, endRow, endCol } = selection;
        const minRow = Math.min(startRow, endRow);
        const maxRow = Math.max(startRow, endRow);
        const minCol = Math.min(startCol, endCol);
        const maxCol = Math.max(startCol, endCol);
        
        // Her seçili hücreye formülü uygula
        for (let row = minRow; row <= maxRow; row++) {
          for (let col = minCol; col <= maxCol; col++) {
            // Formülü bu hücre için adapte et
            const adaptedFormula = adaptFormulaForCell(formula, row, col, minRow, minCol);
            
            // Hücreyi güncelle
            let finalValue;
            try {
              finalValue = evaluateFormula(adaptedFormula, newGrid);
            } catch (error) {
              finalValue = '#ERROR!';
            }
            
            newGrid[row][col] = createCell(finalValue, adaptedFormula, newGrid[row][col]?.format);
          }
        }
        
        // Tüm bağımlı hücreleri yeniden hesapla
        recalculateAllFormulas(newGrid);
        
        newSheets[activeSheetIndex] = {
          ...sheet,
          grid: newGrid
        };
        
        set({ sheets: newSheets });
      },

      // YENİ: Reactive calculation - bağımlılık grafiği ile hesaplama
      recalculateWithDependencies: () => {
        const { sheets, activeSheetIndex } = get();
        const newSheets = [...sheets];
        const sheet = newSheets[activeSheetIndex];
        const newGrid = sheet.grid.map(row => [...row]);
        
        try {
          // Bağımlılık grafiği oluştur
          const dependencies = buildDependencyGraph(newGrid);
          
          // Hesaplama sırasını belirle
          const calculationOrder = getCalculationOrder(dependencies);
          
          // Sırayla hesapla
          calculationOrder.forEach(cellKey => {
            const [row, col] = cellKey.split('-').map(Number);
            const cell = newGrid[row][col];
            
            if (cell && cell.formula) {
              try {
                const newValue = evaluateFormula(cell.formula, newGrid);
                newGrid[row][col].value = newValue;
              } catch (error) {
                newGrid[row][col].value = '#ERROR!';
              }
            }
          });
          
          newSheets[activeSheetIndex] = {
            ...sheet,
            grid: newGrid
          };
          
          set({ sheets: newSheets });
        } catch (error) {
          console.error('Circular reference detected:', error);
          // Circular reference durumunda normal hesaplama yap
          recalculateAllFormulas(newGrid);
          newSheets[activeSheetIndex] = {
            ...sheet,
            grid: newGrid
          };
          set({ sheets: newSheets });
        }
      },

      // YENİ: Enter tuşu ile formül uygulama
      applyFormulaOnEnter: (formula) => {
        const { sheets, activeSheetIndex } = get();
        const sheet = sheets[activeSheetIndex];
        const { selection } = sheet;
        
        // Tek hücre seçimi
        if (selection.startRow === selection.endRow && selection.startCol === selection.endCol) {
          get().updateCell(selection.startRow, selection.startCol, formula);
        } else {
          // Çoklu hücre seçimi
          get().applyFormulaToSelection(formula);
        }
      },

      // YENİ: Formula bar değerini güncelle
      setFormulaBarValue: (value) => {
        set({ formulaBarValue: value });
      },

      // YENİ: Seçili hücrelerin formülünü al
      getSelectionFormula: () => {
        const { sheets, activeSheetIndex } = get();
        const sheet = sheets[activeSheetIndex];
        const { selection } = sheet;
        
        // Tek hücre seçimi
        if (selection.startRow === selection.endRow && selection.startCol === selection.endCol) {
          const cell = sheet.grid[selection.startRow]?.[selection.startCol];
          return cell?.formula || cell?.value || '';
        }
        
        // Çoklu hücre seçimi - ilk hücrenin formülünü döndür
        const cell = sheet.grid[selection.startRow]?.[selection.startCol];
        return cell?.formula || cell?.value || '';
      },

      // Başlık ve açıklama güncelleme
      setTableInfo: (title, description) => {
        set({ tableTitle: title, tableDescription: description });
      },

      setTableTitle: (title) => {
        set({ tableTitle: title });
      },

      setTableDescription: (description) => {
        set({ tableDescription: description });
      },

      setSheets: (newSheets) => {
        // Sheets array'ini kontrol et ve her sheet için gerekli özelliklerin olduğundan emin ol
        const safeSheets = Array.isArray(newSheets) ? newSheets.map((sheet, index) => ({
          id: sheet.id || `sheet_${index}`,
          name: sheet.name || `Sayfa ${index + 1}`,
          grid: Array.isArray(sheet.grid) ? sheet.grid : createEmptyGrid(),
          activeCell: sheet.activeCell || { row: 0, col: 0 },
          selection: sheet.selection || { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
          viewport: sheet.viewport || { startRow: 0, startCol: 0, endRow: 50, endCol: 20 },
          columnWidths: sheet.columnWidths || {},
          rowHeights: sheet.rowHeights || {}
        })) : [{
          id: 'sheet_1',
          name: 'Sayfa 1',
          grid: createEmptyGrid(),
          activeCell: { row: 0, col: 0 },
          selection: { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
          viewport: { startRow: 0, startCol: 0, endRow: 50, endCol: 20 },
          columnWidths: {},
          rowHeights: {}
        }];
        
        console.log('setSheets: Safe sheets oluşturuldu:', safeSheets.length, 'adet');
        set({ sheets: safeSheets, activeSheetIndex: 0 });
      },

      // Tabloyu API'ye kaydetme
      saveTableToAPI: async (title, description = '', providedToken = null, folderId = '0') => {
        console.log('saveTableToAPI başladı');
        console.log('title:', title);
        console.log('description:', description);
        console.log('folderId (parentId):', folderId);
        console.log('folderId type:', typeof folderId);
        console.log('folderId value debug:', JSON.stringify(folderId));
        
        // Store'dan verileri al
        const { sheets, currentFormId } = get();
        // ParentId'yi string olarak kullan (API string bekliyor)
        const parentId = folderId ? folderId.toString() : '0';
        console.log('sheets:', sheets);
        console.log('currentFormId:', currentFormId);
        console.log('Final parentId:', parentId);
        console.log('Final parentId type:', typeof parentId);
        console.log('Final parentId debug:', JSON.stringify(parentId));
        
        try {
          
          // Token'ı al
          let appToken = providedToken;
          let userData = {};
          let userId = '';
          
          // Eğer token parametre olarak verilmemişse, localStorage'dan al
          if (!appToken) {
            appToken = localStorage.getItem('appToken') || '';
          }
          
          // Eğer hala yoksa, applicationToken'dan almaya çalış
          if (!appToken) {
            try {
              const applicationToken = localStorage.getItem('applicationToken');
              if (applicationToken) {
                appToken = JSON.parse(applicationToken);
              }
            } catch (error) {
              console.error('ApplicationToken parse hatası:', error);
            }
          }
          
          // Eğer hala yoksa, Redux store'dan almaya çalış
          if (!appToken) {
            try {
              const reduxState = JSON.parse(localStorage.getItem('reduxState') || '{}');
              appToken = reduxState.appToken?.apptoken || '';
            } catch (error) {
              console.error('Redux state parse hatası:', error);
            }
          }
          
          try {
            const storedUserData = localStorage.getItem('userData');
            if (storedUserData) {
              userData = JSON.parse(storedUserData);
              userId = userData.id || '';
            }
          } catch (error) {
            console.error('User data parse hatası:', error);
          }
          
          console.log('Token Debug:', {
            localStorageToken: localStorage.getItem('appToken'),
            reduxToken: appToken,
            userData: userData,
            userId: userId
          });
          
          // Token kontrolü
          if (!appToken) {
            console.error('ApplicationToken bulunamadı!');
            throw new Error('ApplicationToken bulunamadı. Lütfen tekrar giriş yapın.');
          }
          
          if (!userId) {
            console.error('Kullanıcı bilgisi bulunamadı!');
            throw new Error('Kullanıcı bilgisi bulunamadı. Lütfen tekrar giriş yapın.');
          }
          
          console.log('Token ve kullanıcı bilgileri doğrulandı');
          
          // Yeni formId oluştur (eğer yeni tablo ise)
          const formId = currentFormId || Math.floor(100000 + Math.random() * 900000).toString();
          
          // Debug bilgileri
          console.log('API Request Debug:', {
            appToken: appToken ? 'Token mevcut' : 'Token yok',
            userId: userId,
            endpoint: currentFormId ? `PUT /` : 'POST /',
            currentFormId: currentFormId,
            formId: formId,
            title: title,
            description: description
          });
          
          // Sheets'i hazırla
          console.log('Kaydetme öncesi sheets:', sheets);
          
          const preparedSheets = sheets.map((sheet, index) => {
            console.log(`Sheet ${index} kaydetme öncesi grid:`, sheet.grid);
            console.log(`Sheet ${index} grid satır sayısı:`, sheet.grid ? sheet.grid.length : 'undefined');
            
            // Grid verilerini kontrol et ve hazırla
            let preparedGrid = [];
            if (sheet.grid && Array.isArray(sheet.grid)) {
              console.log(`Sheet ${index} grid hazırlanıyor...`);
              // Grid verilerini temizle ve hazırla
              preparedGrid = sheet.grid.map((row, rowIndex) => {
                if (Array.isArray(row)) {
                  return row.map((cell, colIndex) => {
                    const cellId = `cell_${rowIndex}_${colIndex}`;
                    const cellAddress = (typeof XLSX !== 'undefined' && XLSX.utils && XLSX.utils.encode_cell)
                      ? XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })
                      : `${rowIndex},${colIndex}`;
                    if (cell && typeof cell === 'object') {
                      return {
                        id: cell.id || cellId,
                        row: rowIndex,
                        col: colIndex,
                        address: cell.address || cellAddress,
                        value: cell.value || '',
                        formula: cell.formula || '',
                        format: cell.format || {
                          bold: false,
                          italic: false,
                          underline: false,
                          fontSize: 11,
                          fontFamily: 'Arial',
                          textColor: '#000000',
                          backgroundColor: '#ffffff',
                          horizontalAlign: 'left',
                          verticalAlign: 'top',
                          border: {
                            top: { style: 'none', color: '#000000' },
                            right: { style: 'none', color: '#000000' },
                            bottom: { style: 'none', color: '#000000' },
                            left: { style: 'none', color: '#000000' }
                          }
                        }
                      };
                    } else {
                      return { 
                        id: cellId,
                        row: rowIndex,
                        col: colIndex,
                        address: cellAddress,
                        value: '', 
                        formula: '', 
                        format: {
                          bold: false,
                          italic: false,
                          underline: false,
                          fontSize: 11,
                          fontFamily: 'Arial',
                          textColor: '#000000',
                          backgroundColor: '#ffffff',
                          horizontalAlign: 'left',
                          verticalAlign: 'top',
                          border: {
                            top: { style: 'none', color: '#000000' },
                            right: { style: 'none', color: '#000000' },
                            bottom: { style: 'none', color: '#000000' },
                            left: { style: 'none', color: '#000000' }
                          }
                        }
                      };
                    }
                  });
                } else {
                  return Array(100).fill(null).map((_, colIndex) => ({ 
                    id: `cell_${rowIndex}_${colIndex}`,
                    row: rowIndex,
                    col: colIndex,
                    address: (typeof XLSX !== 'undefined' && XLSX.utils && XLSX.utils.encode_cell)
                      ? XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })
                      : `${rowIndex},${colIndex}`,
                    value: '', 
                    formula: '', 
                    format: {
                      bold: false,
                      italic: false,
                      underline: false,
                      fontSize: 11,
                      fontFamily: 'Arial',
                      textColor: '#000000',
                      backgroundColor: '#ffffff',
                      horizontalAlign: 'left',
                      verticalAlign: 'top',
                      border: {
                        top: { style: 'none', color: '#000000' },
                        right: { style: 'none', color: '#000000' },
                        bottom: { style: 'none', color: '#000000' },
                        left: { style: 'none', color: '#000000' }
                      }
                    }
                  }));
                }
              });
              console.log(`Sheet ${index} grid hazırlandı, satır sayısı:`, preparedGrid.length);
            } else {
              // Grid yoksa boş grid oluştur
              preparedGrid = createEmptyGrid();
            }
            
            return {
              id: sheet.id || `sheet_${index}`,
              name: sheet.name || `Sayfa ${index + 1}`,
              grid: preparedGrid,
              activeCell: sheet.activeCell || { row: 0, col: 0 },
              selection: sheet.selection || { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
              viewport: sheet.viewport || { startRow: 0, startCol: 0, endRow: 50, endCol: 20 },
              columnWidths: sheet.columnWidths || {},
              rowHeights: sheet.rowHeights || {}
            };
          });
          
          console.log('Hazırlanan sheets:', preparedSheets);
          
          // Tablo verilerini hazırla
          const formContentData = {
            id: formId,
            title: title,
            description: description,
            sheets: preparedSheets,
            settings: {},
            isPublished: true,
            publishedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            formLink: `https://panel.formclicker.com/user-login?tableId=${formId}`
          };
          
          // formContent verilerini kontrol et
          console.log('formContentData hazırlandı:', {
            id: formContentData.id,
            title: formContentData.title,
            description: formContentData.description,
            sheetsCount: formContentData.sheets?.length,
            settings: formContentData.settings,
            isPublished: formContentData.isPublished,
            publishedAt: formContentData.publishedAt,
            updatedAt: formContentData.updatedAt,
            formLink: formContentData.formLink
          });
          
          // formContent verilerini doğrula
          if (!formContentData.id) {
            throw new Error('formContentData.id eksik');
          }
          if (!formContentData.title) {
            throw new Error('formContentData.title eksik');
          }
          if (!formContentData.sheets || !Array.isArray(formContentData.sheets)) {
            throw new Error('formContentData.sheets eksik veya geçersiz');
          }
          if (formContentData.sheets.length === 0) {
            throw new Error('formContentData.sheets boş');
          }
          if (!formContentData.settings) {
            throw new Error('formContentData.settings eksik');
          }
          if (!formContentData.isPublished) {
            throw new Error('formContentData.isPublished eksik');
          }
          if (!formContentData.publishedAt) {
            throw new Error('formContentData.publishedAt eksik');
          }
          if (!formContentData.updatedAt) {
            throw new Error('formContentData.updatedAt eksik');
          }
          if (!formContentData.formLink) {
            throw new Error('formContentData.formLink eksik');
          }
          
          console.log('FormContent Data:', formContentData);
          
          const tableData = {
            id: 0,
            title: title,
            description: description,
            formId: formId,
            formContent: formContentData, // OBJECT olarak gönder, çift escape'i önle
            isActive: true,
            isDeleted: false,
            parentId: parentId,
            fileType: 1 // Tablo tipi
          };
          
          // TABLO ÖZEL DEBUG
          console.log('🔥 TABLO KAYDETME DEBUG:');
          console.log('🔥 tableData.parentId:', tableData.parentId);
          console.log('🔥 tableData.fileType:', tableData.fileType);
          console.log('� parentId variable:', parentId);
          console.log('� folderId parameter:', folderId);
          console.log('� Tam tableData:', tableData);
          
          // tableData temel kontrolleri (id 0 olabilir, sadece undefined/null hata)
          if (tableData.id === undefined || tableData.id === null || !tableData.title || !tableData.formContent) {
            throw new Error('tableData temel alanları eksik');
          }
          
          console.log('Kaydedilecek tableData:', tableData);
          console.log('formContent object prepared');
          console.log('formContent sheets count:', formContentData.sheets?.length);
          console.log('formContent sheets:', formContentData.sheets);
          
          console.log('API çağrısı öncesi tableData:', tableData);
          let response;
          if (currentFormId) {
            console.log('Mevcut tablo güncelleniyor:', currentFormId);
            
            // Mevcat tabloyu güncelle için updateData hazırla
            const updateData = {
              id: parseInt(currentFormId), // int olarak gönder
              title: title,
              description: description,
              formId: currentFormId.toString(), // string olarak gönder
              formContent: formContentData, // OBJECT olarak gönder, çift escape'i önle
              isActive: true,
              parentId: parentId
            };
            
            // updateData verilerini kontrol et
            console.log('updateData hazırlandı:', {
              id: updateData.id,
              title: updateData.title,
              description: updateData.description,
              formContentLength: updateData.formContent?.length,
              isActive: updateData.isActive,
              parentId: updateData.parentId,
              fileType: updateData.fileType
            });
            
            console.log('📍 CRITICAL DEBUG - updateData.parentId:', updateData.parentId);
            console.log('📍 CRITICAL DEBUG - updateData.parentId type:', typeof updateData.parentId);
            console.log('📍 CRITICAL DEBUG - updateData.parentId JSON:', JSON.stringify(updateData.parentId));
            
            // updateData temel kontrolleri
            if (updateData.id === undefined || updateData.id === null || !updateData.title || !updateData.formContent) {
              throw new Error('updateData temel alanları eksik');
            }
            
            console.log('Güncelleme için gönderilen data:', updateData);
            console.log('Güncelleme formContent object prepared');
            console.log('Güncelleme formContent sheets count:', formContentData.sheets?.length);
            
            // Mevcut tabloyu güncelle
            const updateHeaders = {
              'ApplicationToken': appToken,
              'User': userId,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            };
            
            console.log('Güncelleme headers:', updateHeaders);
            
            // Headers temel kontrolü
            if (!updateHeaders['ApplicationToken'] || !updateHeaders['User']) {
              throw new Error('Gerekli headers eksik');
            }
            
            if (updateHeaders['ApplicationToken'] === appToken) {
              console.log('ApplicationToken header doğru');
            } else {
              console.warn('ApplicationToken header farklı');
              console.warn('Expected:', appToken);
              console.warn('Actual:', updateHeaders['ApplicationToken']);
              console.warn('ApplicationToken header doğrulaması başarısız');
              console.warn('ApplicationToken header doğrulaması başarısız ve farklı');
              console.warn('ApplicationToken header doğrulaması başarısız ve farklı ve başarısız');
            }
            if (updateHeaders['User'] === userId) {
              console.log('User header doğru');
            } else {
              console.warn('User header farklı');
              console.warn('Expected:', userId);
              console.warn('Actual:', updateHeaders['User']);
              console.warn('User header doğrulaması başarısız');
              console.warn('User header doğrulaması başarısız ve farklı');
              console.warn('User header doğrulaması başarısız ve farklı ve başarısız');
            }
            
            const updateBody = JSON.stringify(updateData);
            console.log('Güncelleme body hazırlandı');
            console.log('- updateData.formContent tipi:', typeof updateData.formContent);
            console.log('- updateData.formContent keys:', Object.keys(updateData.formContent || {}));
            console.log('- Final body length:', updateBody.length);
            
            // Body temel kontrolü
            if (!updateBody || updateBody.length === 0) {
              throw new Error('Güncelleme body boş');
            }
            
            // Temel alanların varlığını kontrol et
            if (!updateBody.includes('"id"') || !updateBody.includes('"title"') || !updateBody.includes('"formContent"')) {
              throw new Error('Güncelleme body temel alanları eksik');
            }
            
            try {
              JSON.parse(updateBody);
              console.log('Güncelleme body JSON parse kontrolü başarılı');
            } catch (parseError) {
              throw new Error(`Güncelleme body JSON parse edilemedi: ${parseError.message}`);
            }
            
            response = await fetch(`https://app.hhoms.com/api/form/Libraries/${currentFormId}`, {
              method: 'PUT',
              headers: updateHeaders,
              body: updateBody
            });
          } else {
            console.log('Yeni tablo oluşturuluyor');
            console.log('Yeni tablo için gönderilen data:', tableData);
            const createFormContentString = JSON.stringify(formContentData);
            console.log('Yeni tablo formContent string length:', createFormContentString.length);
            console.log('Yeni tablo formContent sheets count:', formContentData.sheets?.length);
            
            // Yeni tablo JSON string'i parse ederek kontrol et
            try {
              const createParsedCheck = JSON.parse(createFormContentString);
              console.log('Yeni tablo formContent JSON parse kontrolü başarılı');
            } catch (parseError) {
              console.error('Yeni tablo formContent JSON parse hatası:', parseError);
              throw new Error(`Yeni tablo formContent JSON parse edilemedi: ${parseError.message}`);
            }
            // Yeni tablo oluştur
            const createHeaders = {
              'ApplicationToken': appToken,
              'User': userId,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            };
            
            console.log('Yeni tablo headers:', createHeaders);
            
            // Headers temel kontrolü
            if (!createHeaders['ApplicationToken'] || !createHeaders['User']) {
              throw new Error('Gerekli headers eksik');
            }
            if (createHeaders['ApplicationToken'] === appToken) {
              console.log('ApplicationToken header doğru');
            } else {
              console.warn('ApplicationToken header farklı');
              console.warn('Expected:', appToken);
              console.warn('Actual:', createHeaders['ApplicationToken']);
              console.warn('ApplicationToken header doğrulaması başarısız');
              console.warn('ApplicationToken header doğrulaması başarısız ve farklı');
              console.warn('ApplicationToken header doğrulaması başarısız ve farklı ve başarısız');
            }
            if (createHeaders['User'] === userId) {
              console.log('User header doğru');
            } else {
              console.warn('User header farklı');
              console.warn('Expected:', userId);
              console.warn('Actual:', createHeaders['User']);
              console.warn('User header doğrulaması başarısız');
              console.warn('User header doğrulaması başarısız ve farklı');
              console.warn('User header doğrulaması başarısız ve farklı ve başarısız');
            }
            
            const createBody = JSON.stringify(tableData);
            console.log('🔥 API İSTEK KONTROLÜ:');
            console.log('🔥 Gönderilen JSON:', createBody);
            console.log('🔥 JSON içinde parentId:', createBody.includes('"parentId"'));
            console.log('🔥 JSON içinde fileType:1:', createBody.includes('"fileType":1'));
            
            // JSON'u parse ederek kontrol et
            const parsedBody = JSON.parse(createBody);
            console.log('🔥 Parse edilmiş parentId:', parsedBody.parentId);
            console.log('🔥 Parse edilmiş fileType:', parsedBody.fileType);
            
            response = await fetch('https://app.hhoms.com/api/form/Libraries', {
              method: 'POST',
              headers: createHeaders,
              body: createBody
            });
          }
          console.log('API response:', response);
          console.log('API response status:', response.status);
          console.log('API response statusText:', response.statusText);
          console.log('API response ok:', response.ok);
          console.log('API response url:', response.url);
          console.log('API response headers:', Object.fromEntries(response.headers.entries()));
          console.log('API response type:', response.type);
          console.log('API response redirected:', response.redirected);
          console.log('API response bodyUsed:', response.bodyUsed);
          console.log('API response status range:', Math.floor(response.status / 100) * 100);
          console.log('API response status category:', response.status >= 200 && response.status < 300 ? 'success' : response.status >= 400 && response.status < 500 ? 'client error' : response.status >= 500 ? 'server error' : 'other');
          console.log('API response method:', response.type === 'basic' ? 'basic' : response.type === 'cors' ? 'cors' : response.type === 'default' ? 'default' : response.type === 'error' ? 'error' : response.type === 'opaque' ? 'opaque' : response.type === 'opaqueredirect' ? 'opaqueredirect' : 'unknown');
          console.log('API response is success:', response.status >= 200 && response.status < 300);
          console.log('API response is client error:', response.status >= 400 && response.status < 500);
          console.log('API response is server error:', response.status >= 500);
          console.log('API response is 200:', response.status === 200);
          console.log('API response is 201:', response.status === 201);
          console.log('API response is 400:', response.status === 400);
          console.log('API response is 401:', response.status === 401);
          console.log('API response is 403:', response.status === 403);
          console.log('API response is 404:', response.status === 404);
          console.log('API response is 500:', response.status === 500);
          console.log('API response is 502:', response.status === 502);
          console.log('API response is 503:', response.status === 503);
          console.log('API response is 504:', response.status === 504);
          console.log('API response is 505:', response.status === 505);
          console.log('API response is 0:', response.status === 0);
          console.log('API response is undefined:', response.status === undefined);
          console.log('API response is null:', response.status === null);
          console.log('API response is NaN:', isNaN(response.status));
          console.log('API response is Infinity:', response.status === Infinity);
          console.log('API response is -Infinity:', response.status === -Infinity);
          
          if (!response.ok) {
            let errorText;
            try {
              errorText = await response.text();
            } catch (textError) {
              errorText = 'Response text okunamadı';
            }
            
            console.error('API Response Error:', {
              status: response.status,
              statusText: response.statusText,
              url: response.url,
              type: response.type,
              redirected: response.redirected,
              bodyUsed: response.bodyUsed,
              statusRange: Math.floor(response.status / 100) * 100,
              statusCategory: response.status >= 200 && response.status < 300 ? 'success' : response.status >= 400 && response.status < 500 ? 'client error' : response.status >= 500 ? 'server error' : 'other',
              method: response.type === 'basic' ? 'basic' : response.type === 'cors' ? 'cors' : response.type === 'default' ? 'default' : response.type === 'error' ? 'error' : response.type === 'opaque' ? 'opaque' : response.type === 'opaqueredirect' ? 'opaqueredirect' : 'unknown',
              isSuccess: response.status >= 200 && response.status < 300,
              isClientError: response.status >= 400 && response.status < 500,
              isServerError: response.status >= 500,
              is200: response.status === 200,
              is201: response.status === 201,
              is400: response.status === 400,
              is401: response.status === 401,
              is403: response.status === 403,
              is404: response.status === 404,
              is500: response.status === 500,
              is502: response.status === 502,
              is503: response.status === 503,
              is504: response.status === 504,
              is505: response.status === 505,
              is0: response.status === 0,
              isUndefined: response.status === undefined,
              isNull: response.status === null,
              isNaN: isNaN(response.status),
              isInfinity: response.status === Infinity,
              isNegativeInfinity: response.status === -Infinity,
              headers: Object.fromEntries(response.headers.entries()),
              body: errorText
            });
            
            if (currentFormId) {
              console.error('Güncelleme için gönderilen data:', updateData);
            } else {
              console.error('Yeni tablo için gönderilen data:', tableData);
            }
            
            throw new Error(`Tablo kaydedilemedi: ${response.status} ${response.statusText} - ${errorText}`);
          }
          
          let result;
          try {
            result = await response.json();
            console.log('🔥 API RESPONSE KONTROLÜ:');
            console.log('🔥 Response result:', result);
            console.log('🔥 Response parentId:', result.parentId);
            console.log('🔥 Response fileType:', result.fileType);
            console.log('🔥 Response success:', result.success);
            console.log('🔥 Response data:', result.data);
          } catch (parseError) {
            console.error('Response JSON parse hatası:', parseError);
            const responseText = await response.text();
            console.log('Response text:', responseText);
            console.log('Response text length:', responseText.length);
            console.log('Response text preview:', responseText.substring(0, 200) + '...');
            throw new Error(`Response parse edilemedi: ${parseError.message}`);
          }
          
          // Başarılı kaydetme durumunda currentFormId'yi güncelle
          if (!currentFormId) {
            set({ currentFormId: formId });
            console.log('Yeni tablo için currentFormId güncellendi:', formId);
          }
          
          // Başlık ve açıklama bilgilerini güncelle
          set({ tableTitle: title, tableDescription: description });
          console.log('Tablo bilgileri güncellendi:', { title, description });
          
          console.log('Tablo başarıyla kaydedildi!');
          console.log('Store state after save:', get());
          console.log('API Response Result final:', result);
          console.log('Tablo kaydetme işlemi tamamlandı');
          console.log('Tablo kaydetme işlemi başarılı');
          console.log('Tablo kaydetme işlemi tamamlandı ve sonuç döndürülüyor');
          console.log('Tablo kaydetme işlemi tamamlandı ve başarılı');
          console.log('Tablo kaydetme işlemi tamamlandı ve başarılı ve sonuç döndürülüyor');
          console.log('Tablo kaydetme işlemi tamamlandı ve başarılı ve sonuç döndürülüyor ve tamamlandı');
          console.log('Tablo kaydetme işlemi tamamlandı ve başarılı ve sonuç döndürülüyor ve tamamlandı ve başarılı');
          console.log('Tablo kaydetme işlemi tamamlandı ve başarılı ve sonuç döndürülüyor ve tamamlandı ve başarılı ve tamamlandı');
          console.log('Tablo kaydetme işlemi tamamlandı ve başarılı ve sonuç döndürülüyor ve tamamlandı ve başarılı ve tamamlandı ve başarılı');
          return result;
        } catch (error) {
          console.error('Tablo kaydetme hatası:', error);
          console.error('Hata detayları:', {
            message: error.message,
            stack: error.stack,
            currentFormId: currentFormId || 'tanımlı değil',
            sheets: sheets ? sheets.length : 0,
            title,
            description
          });
          console.error('Store state on error:', get());
          console.error('Error name:', error.name);
          console.error('Error cause:', error.cause);
          console.error('Tablo kaydetme işlemi başarısız');
          console.error('Tablo kaydetme işlemi hata ile sonlandı');
          console.error('Tablo kaydetme işlemi hata fırlatılıyor');
          console.error('Tablo kaydetme işlemi hata ile tamamlandı');
          console.error('Tablo kaydetme işlemi hata ile tamamlandı ve hata fırlatılıyor');
          console.error('Tablo kaydetme işlemi hata ile tamamlandı ve hata fırlatılıyor ve tamamlandı');
          console.error('Tablo kaydetme işlemi hata ile tamamlandı ve hata fırlatılıyor ve tamamlandı ve hata');
          console.error('Tablo kaydetme işlemi hata ile tamamlandı ve hata fırlatılıyor ve tamamlandı ve hata ve tamamlandı');
          console.error('Tablo kaydetme işlemi hata ile tamamlandı ve hata fırlatılıyor ve tamamlandı ve hata ve tamamlandı ve hata');
          throw error;
        }
      },

      // API'den tablo yükleme
      loadSpreadsheetFromAPI: async (formId, initialFormContent = null) => {
        try {
          set({ isLoading: true });
          console.log('loadSpreadsheetFromAPI başladı, formId:', formId);
          console.log('initialFormContent var mı:', !!initialFormContent);
          
          let table;
          
          if (initialFormContent) {
            // SpreadsheetView'den gelen veriyi kullan
            console.log('initialFormContent kullanılıyor');
            table = {
              id: formId,
              formId: formId,
              formContent: initialFormContent,
              title: 'Tablo',
              description: ''
            };
          } else {
            // Normal API çağrısı yap
            const appToken = localStorage.getItem('appToken') || '';
            let userData = {};
            let userId = '';
            
            try {
              const storedUserData = localStorage.getItem('userData');
              if (storedUserData) {
                userData = JSON.parse(storedUserData);
                userId = userData.id || '';
              }
            } catch (error) {
              console.error('User data parse hatası:', error);
            }
            
            // Token kontrolü
            if (!appToken) {
              throw new Error('ApplicationToken bulunamadı. Lütfen tekrar giriş yapın.');
            }
            
            if (!userId) {
              throw new Error('Kullanıcı bilgisi bulunamadı. Lütfen tekrar giriş yapın.');
            }
            
            const response = await fetch(`https://app.hhoms.com/api/form/Libraries/${formId}`, {
              headers: {
                'ApplicationToken': appToken,
                'User': userId
              }
            });
            
            if (!response.ok) {
              const errorText = await response.text();
              console.error('API Response Error:', {
                status: response.status,
                statusText: response.statusText,
                url: response.url,
                body: errorText
              });
              throw new Error(`Tablo yüklenemedi: ${response.status} ${response.statusText} - ${errorText}`);
            }
            
            const data = await response.json();
            console.log('API Response Data:', data);
            console.log('API Response yapısı kontrol ediliyor...');
            
            // API response yapısını kontrol et - formLibraries object mi yoksa array mi?
            
            if (data && data.formLibraries) {
              if (Array.isArray(data.formLibraries)) {
                console.log('FormLibraries array bulundu, uzunluk:', data.formLibraries.length);
                // Array formatı - ilgili form'u bul
                table = data.formLibraries.find(lib => lib.formId === formId || lib.id.toString() === formId);
                
                if (!table && data.formLibraries.length > 0) {
                  // Eğer formId ile bulamazsa, ilk elemanı al
                  table = data.formLibraries[0];
                  console.log('FormId ile bulunamadı, ilk element alındı:', table);
                }
              } else {
                // Object formatı - direkt kullan
                console.log('FormLibraries object bulundu');
                table = data.formLibraries;
              }
            } else if (data && data.id) {
              // Direkt form object'i dönmüş
              table = data;
              console.log('Direkt form object alındı');
            } else {
              console.error('Beklenmeyen API response yapısı:', data);
              throw new Error('Geçersiz API response yapısı');
            }
          }
          
          console.log('Seçilen table:', table);
          console.log('Table formContent type:', typeof table?.formContent);
          console.log('Table formContent length:', table?.formContent?.length);
          
          if (!table || !table.id) {
            throw new Error('Tablo bulunamadı');
          }
          
          if (!table.formContent) {
            console.warn('Tablo formContent boş, boş tablo oluşturulacak');
            // formContent boşsa boş tablo oluştur
            const emptySheet = {
              id: 'sheet_1',
              name: 'Sayfa 1',
              grid: createEmptyGrid(),
              activeCell: { row: 0, col: 0 },
              selection: { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
              viewport: { startRow: 0, startCol: 0, endRow: 50, endCol: 20 },
              columnWidths: {},
              rowHeights: {}
            };
            
            set({ 
              sheets: [emptySheet],
              activeSheetIndex: 0,
              currentFormId: formId,
              isLoaded: true,
              tableTitle: table.title || '',
              tableDescription: table.description || ''
            });
            
            console.log('Boş formContent için tablo oluşturuldu');
            return;
          }
          
          // formContent'i parse et - BASIT TEST VERSION
          let parsedContent;
          try {
            if (typeof table.formContent === 'string') {
              console.log('📋 FORMCONTENT STRING TEST:');
              console.log('Content uzunluk:', table.formContent.length);
              console.log('İlk 100 karakter:', table.formContent.substring(0, 100));
              console.log('Son 100 karakter:', table.formContent.substring(table.formContent.length - 100));
              
              // SINGLE PARSE ATTEMPT
              parsedContent = JSON.parse(table.formContent);
              console.log('✅ PARSE BAŞARILI!');
              console.log('Parse sonucu keys:', Object.keys(parsedContent));
              console.log('Sheets var mı:', !!parsedContent.sheets);
              console.log('Sheets sayısı:', parsedContent.sheets?.length);
              
              if (parsedContent.sheets && parsedContent.sheets[0]) {
                console.log('İlk sheet:', {
                  id: parsedContent.sheets[0].id,
                  name: parsedContent.sheets[0].name,
                  gridVar: !!parsedContent.sheets[0].grid,
                  gridLength: parsedContent.sheets[0].grid?.length
                });
                
                // İlk şeetin ilk birkaç satırını kontrol et
                if (parsedContent.sheets[0].grid && parsedContent.sheets[0].grid.length > 0) {
                  console.log('Grid ilk satır:', parsedContent.sheets[0].grid[0]);
                  console.log('Grid ikinci satır:', parsedContent.sheets[0].grid[1]);
                }
              }
              
            } else {
              parsedContent = table.formContent;
              console.log('FormContent zaten object formatında');
            }
            
          } catch (error) {
            console.error('❌ PARSE HATASI:', error);
            console.error('Hatalı content preview:', table.formContent?.substring(0, 500));
            
            // Parse hatası durumunda boş tablo oluştur
            const emptySheet = {
              id: 'sheet_1',
              name: 'Sayfa 1',
              grid: createEmptyGrid(),
              activeCell: { row: 0, col: 0 },
              selection: { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
              viewport: { startRow: 0, startCol: 0, endRow: 50, endCol: 20 },
              columnWidths: {},
              rowHeights: {}
            };
            
            set({ 
              sheets: [emptySheet],
              activeSheetIndex: 0,
              currentFormId: formId,
              isLoaded: true,
              isLoading: false,
              tableTitle: table.title || '',
              tableDescription: table.description || ''
            });
            
            console.log('Parse hatası nedeniyle boş tablo oluşturuldu');
            return;
          }

          // Sheets'i yükle
          console.log('📊 Parsed Content Sheets kontrol ediliyor...');
          console.log('📋 Full parsed content:', parsedContent);
          console.log('Parsed Content keys:', Object.keys(parsedContent || {}));
          console.log('Sheets exists:', !!parsedContent.sheets);
          console.log('Sheets type:', typeof parsedContent.sheets);
          console.log('Sheets is array:', Array.isArray(parsedContent.sheets));
          console.log('Sheets length:', parsedContent.sheets?.length);
          console.log('Sheets content preview:', parsedContent.sheets);
          
          if (parsedContent.sheets && Array.isArray(parsedContent.sheets) && parsedContent.sheets.length > 0) {
            console.log('✅ Sheets array bulundu, yükleniyor...');
            console.log('Sheets array length:', parsedContent.sheets.length);
            
            const newSheets = parsedContent.sheets.map((sheet, index) => {
              console.log(`📋 Sheet ${index} işleniyor:`, {
                id: sheet.id,
                name: sheet.name,
                gridExists: !!sheet.grid,
                gridLength: sheet.grid?.length,
                fullSheet: sheet
              });
              
              // Grid verilerini kontrol et ve restore et
              let restoredGrid;
              console.log(`🔍 Sheet ${index} grid analizi:`);
              console.log(`- Grid exists:`, !!sheet.grid);
              console.log(`- Grid is array:`, Array.isArray(sheet.grid));
              console.log(`- Grid length:`, sheet.grid?.length);
              console.log(`- Grid sample:`, sheet.grid?.slice(0, 2)); // İlk 2 satırı göster
              
              if (sheet.grid && Array.isArray(sheet.grid) && sheet.grid.length > 0) {
                console.log(`✅ Sheet ${index} grid restore ediliyor...`);
                console.log(`📊 Grid satır sayısı:`, sheet.grid.length);
                
                // İlk satırın içeriğini kontrol et
                if (sheet.grid[0]) {
                  console.log(`📋 İlk satır tipi:`, typeof sheet.grid[0]);
                  console.log(`📋 İlk satır array mi:`, Array.isArray(sheet.grid[0]));
                  console.log(`📋 İlk satır uzunluk:`, sheet.grid[0]?.length);
                  console.log(`📋 İlk satır sample:`, sheet.grid[0]?.slice(0, 3));
                }
                
                restoredGrid = sheet.grid.map((row, rowIndex) => {
                  if (Array.isArray(row)) {
                    return row.map((cell, colIndex) => {
                      // Her hücreyi detaylı kontrol et
                      if (cell && typeof cell === 'object') {
                        // Eğer hücrede veri varsa logla
                        if (cell.value || cell.formula) {
                          console.log(`📝 Veri bulunan hücre [${rowIndex},${colIndex}]:`, {
                            value: cell.value,
                            formula: cell.formula
                          });
                        }
                        
                        return {
                          value: cell.value || '',
                          formula: cell.formula || '',
                          format: cell.format || {
                            bold: false,
                            italic: false,
                            underline: false,
                            fontSize: 11,
                            fontFamily: 'Arial',
                            textColor: '#000000',
                            backgroundColor: '#ffffff',
                            horizontalAlign: 'left',
                            verticalAlign: 'top',
                            border: {
                              top: { style: 'none', color: '#000000' },
                              right: { style: 'none', color: '#000000' },
                              bottom: { style: 'none', color: '#000000' },
                              left: { style: 'none', color: '#000000' }
                            }
                          }
                        };
                      } else {
                        return createCell();
                      }
                    });
                  } else {
                    console.warn(`⚠️ Sheet ${index} satır ${rowIndex} array değil:`, typeof row, row);
                    return Array(100).fill(null).map(() => createCell());
                  }
                });
                
                console.log(`✅ Sheet ${index} grid restore edildi:`);
                console.log(`- Restore edilen satır sayısı:`, restoredGrid.length);
                console.log(`- İlk satır uzunluk:`, restoredGrid[0]?.length);
                
                // Veri olan hücreleri say
                let dataCount = 0;
                restoredGrid.forEach((row, r) => {
                  row.forEach((cell, c) => {
                    if (cell.value || cell.formula) dataCount++;
                  });
                });
                console.log(`📊 Toplam veri olan hücre sayısı:`, dataCount);
                
              } else {
                console.log(`❌ Sheet ${index} için boş grid oluşturuluyor`);
                console.log(`Sebep: grid=${!!sheet.grid}, isArray=${Array.isArray(sheet.grid)}, length=${sheet.grid?.length}`);
                restoredGrid = createEmptyGrid();
              }
              
              return {
                id: sheet.id || `sheet_${index}`,
                name: sheet.name || `Sayfa ${index + 1}`,
                grid: restoredGrid,
                activeCell: sheet.activeCell || { row: 0, col: 0 },
                selection: sheet.selection || { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
                viewport: sheet.viewport || { startRow: 0, startCol: 0, endRow: 50, endCol: 20 },
                columnWidths: sheet.columnWidths || {},
                rowHeights: sheet.rowHeights || {}
              };
            });
            
            console.log('New Sheets:', newSheets);
            console.log('New Sheets length:', newSheets.length);
            console.log('🔄 New sheets array hazırlandı');
            console.log('New sheets count:', newSheets.length);
            console.log('First sheet grid length:', newSheets[0]?.grid?.length);
            
            set({ 
              sheets: newSheets,
              activeSheetIndex: 0,
              currentFormId: formId,
              isLoaded: true,
              isLoading: false,
              tableTitle: parsedContent.title || table.title || '',
              tableDescription: parsedContent.description || ''
            });
            
            console.log('✅ Store başarıyla güncellendi!');
            console.log('📊 Final sheets count:', newSheets.length);
            console.log('🔧 isLoaded:', true, 'isLoading:', false);
          } else {
            console.log('Sheets bulunamadı veya boş, boş tablo oluşturuluyor');
            console.log('Parsed content keys:', Object.keys(parsedContent));
            console.log('Parsed content:', parsedContent);
            console.log('Sheets exists:', !!parsedContent.sheets);
            console.log('Sheets is array:', Array.isArray(parsedContent.sheets));
            console.log('Sheets length:', parsedContent.sheets?.length);
            
            // Sheets yoksa veya boşsa boş tablo oluştur
            const emptySheet = {
              id: 'sheet_1',
              name: 'Sayfa 1',
              grid: createEmptyGrid(),
              activeCell: { row: 0, col: 0 },
              selection: { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
              viewport: { startRow: 0, startCol: 0, endRow: 50, endCol: 20 },
              columnWidths: {},
              rowHeights: {}
            };
            
            console.log('Boş tablo oluşturuluyor:', emptySheet);
            
            set({ 
              sheets: [emptySheet],
              activeSheetIndex: 0,
              currentFormId: formId,
              isLoaded: true,
              isLoading: false,
              tableTitle: parsedContent.title || table.title || '',
              tableDescription: parsedContent.description || ''
            });
            
            console.log('Boş tablo store\'a yüklendi');
          }
        } catch (error) {
          console.error('Tablo yükleme hatası:', error);
          console.error('Hata detayları:', {
            message: error.message,
            stack: error.stack,
            formId
          });
          
          // Hata durumunda boş tablo oluştur
          const errorSheet = {
            id: 'sheet_1',
            name: 'Sayfa 1',
            grid: createEmptyGrid(),
            activeCell: { row: 0, col: 0 },
            selection: { startRow: 0, startCol: 0, endRow: 0, endCol: 0 },
            viewport: { startRow: 0, startCol: 0, endRow: 50, endCol: 20 },
            columnWidths: {},
            rowHeights: {}
          };
          
          console.log('Hata durumunda boş tablo oluşturuluyor:', errorSheet);
          
          set({ 
            sheets: [errorSheet],
            activeSheetIndex: 0,
            currentFormId: formId,
            isLoaded: true,
            isLoading: false,
            tableTitle: '',
            tableDescription: ''
          });
          
          console.log('Hata durumunda boş tablo store\'a yüklendi');
        }
      },

      // API'ye tablo kaydetme
      saveSpreadsheetToAPI: async (formId) => {
        try {
          const { sheets, tableTitle, tableDescription } = get();
          
          // Redux store'dan token'ları al
          const appToken = localStorage.getItem('appToken') || '';
          let userData = {};
          let userId = '';
          
          try {
            const storedUserData = localStorage.getItem('userData');
            if (storedUserData) {
              userData = JSON.parse(storedUserData);
              userId = userData.id || '';
            }
          } catch (error) {
            console.error('User data parse hatası:', error);
          }
          
          // Token kontrolü
          if (!appToken) {
            throw new Error('ApplicationToken bulunamadı. Lütfen tekrar giriş yapın.');
          }
          
          if (!userId) {
            throw new Error('Kullanıcı bilgisi bulunamadı. Lütfen tekrar giriş yapın.');
          }
          
          // Tablo verilerini hazırla
          const formContent = {
            id: formId,
            title: tableTitle,
            description: tableDescription,
            sheets: sheets,
            settings: {
              theme: 'default',
              gridLines: true,
              headers: true,
              autoSave: true
            },
            isPublished: true,
            publishedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          const tableData = {
            id: parseInt(formId),
            title: tableTitle,
            description: tableDescription,
            formId: formId,
            formContent: JSON.stringify(formContent), // String olarak gönder
            isActive: true,
            parentId: '0'
          };

          const response = await fetch(`https://app.hhoms.com/api/form/Libraries/${formId}`, {
            method: 'PUT',
            headers: {
              'ApplicationToken': appToken,
              'User': userId,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(tableData)
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error('API Save Error:', {
              status: response.status,
              statusText: response.statusText,
              url: response.url,
              body: errorText
            });
            throw new Error(`Tablo kaydedilemedi: ${response.status} ${response.statusText}`);
          }

          console.log('Tablo başarıyla kaydedildi');
          return true;
        } catch (error) {
          console.error('Save spreadsheet error:', error);
          throw error;
        }
      }
    }),
    {
      name: 'spreadsheet-store'
    }
  )
);
